import { Clock, Download, Filter, Pencil, Trash2, FileSpreadsheet, ClipboardList } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { NumericInput } from "../components/ui/numeric-input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { ReportExportButton } from "../components/ui/report-export-button";
import { useHoursReport } from "../hooks/useHoursReport";

const HoursReportPage = () => {
  const {
    report, loading, canEdit, printRef,
    startDate, endDate, editDialogOpen, editingEntry,
    editForm, submitting, availableDates, materialsByCategory,
    exportDialogOpen, reportTypeDialogOpen, payrollExportDialogOpen,
    setStartDate, setEndDate, setEditForm,
    setExportDialogOpen, setReportTypeDialogOpen, setPayrollExportDialogOpen,
    fetchReport, handleEditClick, handleDateSelect, handleDeleteEntry,
    handleSaveEdit, handlePayrollExport, closeEditDialog, resetToThisWeek,
    getExcelData, getPdfContent, getPayrollPdfContent,
    getWeeksData, formatDate, getWeekRange,
  } = useHoursReport();

  const weeks = getWeeksData();
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading report...</div>
      </div>
    );
  }

  return (
    <div className="page-enter space-y-8" data-testid="hours-report-page">
      <style hidden>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
          .job-card { page-break-inside: avoid; break-inside: avoid; margin-bottom: 20px; }
          .week-section { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>

      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">Hours Report</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setReportTypeDialogOpen(true)} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="no-print">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="form-group">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date-filter" />
            </div>
            <div className="form-group">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date-filter" />
            </div>
            <Button onClick={fetchReport} data-testid="apply-filter-btn">
              <Filter className="w-4 h-4 mr-2" />Apply Filter
            </Button>
            <Button variant="outline" onClick={resetToThisWeek} data-testid="this-week-btn">
              This Week
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report */}
      <div className="print-area" ref={printRef}>
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold">V2R Masonry - Hours Report</h1>
          <p className="text-sm text-gray-500">Generated: {new Date().toLocaleDateString()}</p>
        </div>

        {report.length === 0 || weeks.length === 0 ? (
          <Card className="p-12">
            <div className="text-center">
              <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No data</h3>
              <p className="text-muted-foreground">No hours recorded for the selected period</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-8">
            {report.map((job) => (
              <Card key={job.job_id} className="overflow-hidden job-card">
                <CardHeader className="bg-muted/50 py-4">
                  <CardTitle className="text-xl">{job.job_name}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {weeks.map((week, weekIndex) => {
                    const hasHoursThisWeek = job.employees.some((emp) =>
                      week.some((d) => emp.hours_by_day[d] > 0)
                    );
                    if (!hasHoursThisWeek) return null;

                    return (
                      <div key={weekIndex} className="border-b border-border last:border-0">
                        <div className="bg-muted/30 px-4 py-2 font-medium text-sm text-muted-foreground">
                          Week of {getWeekRange(week)}
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-border bg-muted/10">
                                <th className="text-left py-3 px-4 font-semibold min-w-[150px]">Employee</th>
                                {dayNames.map((day, i) => (
                                  <th key={day} className="text-center py-3 px-2 font-semibold min-w-[70px]">
                                    <div className="text-xs text-muted-foreground">{day}</div>
                                    <div className="text-xs">{formatDate(week[i])}</div>
                                  </th>
                                ))}
                                <th className="text-right py-3 px-4 font-semibold min-w-[80px]">Total</th>
                                {canEdit && <th className="text-center py-3 px-2 font-semibold min-w-[80px] no-print">Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {job.employees.map((emp) => {
                                const weekHours = week.map((d) => emp.hours_by_day[d] || 0);
                                const weekTotal = weekHours.reduce((a, b) => a + b, 0);
                                if (weekTotal === 0) return null;

                                return (
                                  <tr key={emp.employee_id} className="border-b border-border last:border-0 hover:bg-muted/20">
                                    <td className="py-3 px-4 font-medium">{emp.employee_name}</td>
                                    {weekHours.map((hours, i) => (
                                      <td key={i} className="text-center py-3 px-2">
                                        {hours > 0 ? (
                                          <span className="inline-block bg-primary/10 text-primary font-medium px-2 py-1 rounded min-w-[40px]">{hours}</span>
                                        ) : (
                                          <span className="text-muted-foreground">-</span>
                                        )}
                                      </td>
                                    ))}
                                    <td className="text-right py-3 px-4 font-bold text-primary">{weekTotal}</td>
                                    {canEdit && (
                                      <td className="text-center py-3 px-2 no-print">
                                        <div className="flex items-center justify-center gap-1">
                                          <Button
                                            variant="ghost" size="icon" className="h-7 w-7 text-primary hover:text-primary"
                                            onClick={() => handleEditClick(job.job_id, job.job_name, emp.employee_id, emp.employee_name, emp.hours_by_day, week)}
                                            data-testid={`edit-hours-${emp.employee_id}`}
                                          >
                                            <Pencil className="w-4 h-4" />
                                          </Button>
                                          <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" data-testid={`delete-hours-${emp.employee_id}`}>
                                                <Trash2 className="w-4 h-4" />
                                              </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                              <AlertDialogHeader>
                                                <AlertDialogTitle>Delete Hours Entry?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                  This will remove all hours for {emp.employee_name} on this job for the selected week. This action cannot be undone.
                                                </AlertDialogDescription>
                                              </AlertDialogHeader>
                                              <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction
                                                  onClick={() => {
                                                    const dayWithHours = week.find((d) => emp.hours_by_day[d] > 0);
                                                    if (dayWithHours) handleDeleteEntry(job.job_id, emp.employee_id, dayWithHours);
                                                  }}
                                                  className="bg-destructive text-destructive-foreground"
                                                >Delete</AlertDialogAction>
                                              </AlertDialogFooter>
                                            </AlertDialogContent>
                                          </AlertDialog>
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr className="bg-muted/30 font-semibold">
                                <td className="py-3 px-4">Daily Totals</td>
                                {week.map((date, i) => {
                                  const dayTotal = job.employees.reduce((sum, emp) => sum + (emp.hours_by_day[date] || 0), 0);
                                  return <td key={i} className="text-center py-3 px-2">{dayTotal > 0 ? dayTotal : "-"}</td>;
                                })}
                                <td className="text-right py-3 px-4 text-primary">
                                  {job.employees.reduce((sum, emp) => sum + week.reduce((s, d) => s + (emp.hours_by_day[d] || 0), 0), 0)}
                                </td>
                                {canEdit && <td className="no-print"></td>}
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    );
                  })}

                  <div className="bg-primary/5 px-4 py-3 flex justify-between items-center">
                    <span className="font-semibold">Job Total Hours</span>
                    <span className="text-2xl font-bold text-primary">
                      {job.employees.reduce((sum, emp) => sum + emp.total_hours, 0)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <ReportExportButton
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        showButton={false}
        title="Export Hours Report"
        getExcelData={getExcelData}
        getPdfContent={getPdfContent}
      />

      <ReportExportButton
        open={payrollExportDialogOpen}
        onOpenChange={setPayrollExportDialogOpen}
        showButton={false}
        title="Export Payroll Report"
        getExcelData={() => {
          handlePayrollExport();
          return null;
        }}
        getPdfContent={getPayrollPdfContent}
      />

      {/* Report Type Chooser Dialog */}
      <Dialog open={reportTypeDialogOpen} onOpenChange={setReportTypeDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Export Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Choose report type:</p>
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                className="h-24 flex-col gap-2"
                onClick={() => {
                  setReportTypeDialogOpen(false);
                  setExportDialogOpen(true);
                }}
                data-testid="export-hours-report-btn"
              >
                <Clock className="w-8 h-8 text-blue-600" />
                <span className="text-sm font-medium">Hours Report</span>
              </Button>
              <Button
                variant="outline"
                className="h-24 flex-col gap-2"
                onClick={() => {
                  setReportTypeDialogOpen(false);
                  setPayrollExportDialogOpen(true);
                }}
                data-testid="export-payroll-report-btn"
              >
                <ClipboardList className="w-8 h-8 text-green-600" />
                <span className="text-sm font-medium">Payroll Report</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Hours Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { if (!open) closeEditDialog(); else setEditForm(editForm); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Hours Entry</DialogTitle>
          </DialogHeader>
          {editingEntry && (
            <div className="space-y-4 pt-4">
              <div className="bg-muted/30 p-3 rounded-lg space-y-1">
                <p className="font-medium">{editingEntry.employeeName}</p>
                <p className="text-sm text-muted-foreground">{editingEntry.jobName}</p>
              </div>

              <div className="form-group">
                <Label>Select Day to Edit</Label>
                <Select value={editForm.date} onValueChange={handleDateSelect}>
                  <SelectTrigger data-testid="edit-date-select"><SelectValue placeholder="Select a day..." /></SelectTrigger>
                  <SelectContent>
                    {availableDates.map((item) => (
                      <SelectItem key={item.date} value={item.date}>
                        {formatDate(item.date)} - {item.hours} hours
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {editForm.date && (
                <>
                  <div className="form-group">
                    <Label>Hours Worked</Label>
                    <NumericInput
                      decimals={2} min={0} max={24}
                      value={editForm.hours}
                      onChange={(e) => setEditForm({ ...editForm, hours: e.target.value })}
                      placeholder="Enter hours" data-testid="edit-hours-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Materials (Optional)</Label>
                    {editForm.materials.map((mat, index) => (
                      <div key={index} className="grid grid-cols-2 gap-2">
                        <Select
                          value={mat.material_id}
                          onValueChange={(v) => {
                            const updated = [...editForm.materials];
                            updated[index].material_id = v;
                            setEditForm({ ...editForm, materials: updated });
                          }}
                        >
                          <SelectTrigger><SelectValue placeholder="Material..." /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(materialsByCategory).sort(([a], [b]) => a.localeCompare(b)).map(([category, mats]) => (
                              <div key={category}>
                                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">{category}</div>
                                {[...mats].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((m) => (
                                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                                ))}
                              </div>
                            ))}
                          </SelectContent>
                        </Select>
                        <NumericInput
                          decimals={2} min={0}
                          value={mat.quantity}
                          onChange={(e) => {
                            const updated = [...editForm.materials];
                            updated[index].quantity = e.target.value;
                            setEditForm({ ...editForm, materials: updated });
                          }}
                          placeholder="Qty"
                        />
                      </div>
                    ))}
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => setEditForm({ ...editForm, materials: [...editForm.materials, { material_id: "", quantity: "" }] })}
                    >+ Add Material</Button>
                  </div>
                </>
              )}

              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={closeEditDialog} className="flex-1">Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={submitting || !editForm.hours || !editForm.date} className="flex-1" data-testid="save-edit-btn">
                  {submitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HoursReportPage;
